import {ReportPopupContainer} from "@/utils/presenters/interfaces.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import LinkUtils from "@/utils/common/LinkUtils.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import Translator from "@/utils/common/Translator.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaPrinting from "@/utils/common/NamaPrinting.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import RepOutputFormat from "@/utils/constants/RepOutputFormat.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import {AxiosRequestConfig} from "axios";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import {QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import {nextTick, Ref, ref} from "vue";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {Dialog} from "quasar";
import ReportPagesProgressDialog from "@/components/dialogs/ReportPagesProgressDialog.vue";
import ReportLauncherPresenter from "@/utils/presenters/ReportLauncherPresenter.ts";
import QuestionField = namaTypes.QuestionField;
import LaunchReportRequest = namaTypes.LaunchReportRequest;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import KeyList = namaTypes.KeyList;
import DTOEmailSendRequest = namaTypes.DTOEmailSendRequest;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import ByIdRequest = namaTypes.ByIdRequest;
import PrintingParams = namaTypes.PrintingParams;
import ReportMetadataContainer = namaTypes.ReportMetadataContainer;

export class EmailSenderDialogType {
  static readonly SendReport = "SendReport";
  static readonly SendForm = "SendForm";
}

export default class ReportViewerPresenter extends AbsPresenter
{
  popupContainer?: ReportPopupContainer;
  tempPrintId: string = '';
  printerName: string = '';
  reportId: string = '';
  reportMetaDataContainer?: ReportMetadataContainer;
  readonly reportUrl: Ref<string> = ref('');

  constructor(place: ReportViewerPlace, popupContainer?: ReportPopupContainer) {
    super(place)
    this.popupContainer = popupContainer;
  }

  override fetchPlace(): ReportViewerPlace {
    return this.place as ReportViewerPlace;
  }

  async start() {
    if (this.fetchPlace().tempPrintId)
      await this.onResultReady([{tempPrintId: this.fetchPlace().tempPrintId}]);
    else
      await this.runAndGetTempPrintId();
    this.updateWindowTitle();
  }

  private syncPlaceToRoute() {
    if (this.popupContainer || !this.routeIsShowingThisPlace())
      return;
    router.replace({name: RouteNames.REPORT_RESULT_ROUTE, query: this.fetchPlace().fetchQuery()});
  }

  private routeIsShowingThisPlace(): boolean {
    let currentRoute = router.currentRoute.value;
    if (currentRoute.name !== RouteNames.REPORT_RESULT_ROUTE)
      return false;
    if (String(currentRoute.query[PlaceTokens.PRM_ID] ?? '') !== (this.fetchPlace().reportId ?? ''))
      return false;
    return String(currentRoute.query[PlaceTokens.PRM_QUESTIONS] ?? '') === (this.fetchPlace().questions ?? '');
  }

  updateWindowTitle() {
    if (!this.popupContainer)
      GUIUtilities.updateWindowTitle(this.place?.getTitleForWindow() ?? '');
  }

  async runAndExport(format: string, downloadPurpose: string) {
    await this.runAndGetTempPrintId(async () => {
      await this.onExport(format, downloadPurpose);
    });
  }

  async runAndGetTempPrintId(afterReady?: () => void | Promise<void>) {
    let request: LaunchReportRequest = {
      questions: this.fetchPlace().questions,
      reportId: this.fetchPlace().reportId,
      outputFormat: this.fetchPlace().format ?? '',
      printingOption: this.fetchPlace().printingOption ?? '',
      downloadPurpose: this.fetchPlace().purpose ?? '',
      runAsynchronous: true
    };
    let settings: NamaAxiosRequestConfig = {
      data: request,
      noLoadingIndicator: true
    };
    let response = await NamaCommon.namaFetch(NamaURLS.RunReport, settings)
    if (response && response.result && response.result.succeded) {
      let params: PrintingParams | undefined = response.list[0] ?? undefined;
      if (params && params.taskId && !params.processed) {
        let taskId = params.taskId;
        Dialog.create({
          component: ReportPagesProgressDialog,
          componentProps: {taskId}
        }).onOk(async (asyncResult: any) => {
          if (asyncResult && asyncResult.result && asyncResult.result.succeded)
            await this.onResultReady(asyncResult.list, afterReady);
        }).onCancel(async () => {
          await NamaCommon.namaFetch(NamaURLS.KillReport, {params: {taskId}})
        });
      } else {
        await this.onResultReady(response.list, afterReady);
      }
    }
  }

  private async onResultReady(result: any[], afterReady?: () => void | Promise<void>) {
    this.tempPrintId = result[0].tempPrintId ?? '';
    this.reportId = this.fetchPlace().reportId ?? '';
    this.printerName = result[0].printerName ?? '';
    this.reportUrl.value = ReportViewerPresenter.rptUrl(this.reportId, this.tempPrintId, '', this.fetchPlace().format ?? '', this.fetchPlace().printingOption ?? '',
      this.fetchPlace().purpose ?? '', this.printerName);
    this.rememberResultInPlace();
    if (afterReady)
      await afterReady();
  }

  private rememberResultInPlace() {
    if (!this.tempPrintId || this.fetchPlace().tempPrintId === this.tempPrintId)
      return;
    this.fetchPlace().tempPrintId = this.tempPrintId;
    this.syncPlaceToRoute();
  }

  static rptUrl(reportId: string | undefined, tempPrintId: string, questions: string, format: string, printingOption: string, purpose: string, printerName: string) {
    let url = CommonConstants.RUN_REPORT_SERVLET + LinkUtils.requestParam(PlaceTokens.PRM_QUESTIONS, questions)
      + LinkUtils.requestParam(PlaceTokens.PRM_OUTPUT_FORMAT, format) + LinkUtils.requestParam(PlaceTokens.PRM_ID, reportId)
      + LinkUtils.requestParam(PlaceTokens.PRM_PRINTING_OPTION, printingOption) + LinkUtils.requestParam(PlaceTokens.PRM_DOWNLOAD_PURPOSE, purpose)
      + LinkUtils.requestParam(PlaceTokens.PRM_TEMP_PRINT_ID, tempPrintId) +
      +LinkUtils.requestParam(PlaceTokens.PRM_PRINTER_NAME, printerName) + LinkUtils.requestParam("timeStamp", ClientCache.currentTimeMillis())
      + LinkUtils.requestParam(PlaceTokens.PRM_LANGUAGE, Translator.currentLang());
    url = NamaPrinting.addDimensionsParametersToURL(url);
    if (ClientCache.getCustomGUISettings()?.openPrintInBrowserWindow)
      url += LinkUtils.requestParam("newP", "yesToNewPNama");
    return url;
  }

  async relaunchReport() {
    let launcherPlace: ReportLauncherPlace = new ReportLauncherPlace();
    launcherPlace.reportId = this.fetchPlace().reportId;
    launcherPlace.questions = this.fetchPlace().questions;
    launcherPlace.format = this.fetchPlace().format;
    if (this.popupContainer) {
      let questions: DTOFilledQuestionField[] = new QuestionFieldsSerializer().questionsFromURL(launcherPlace.questions);
      questions = EmbeddedViewUtils.overrideQuestionsIfNeeded(questions, this.popupContainer);
      launcherPlace.questions = new QuestionFieldsSerializer().toURL(questions)
      this.popupContainer.place = launcherPlace;
      this.popupContainer.currentMode.value = "launch";
    } else {
      await router.push({
        name: RouteNames.LAUNCH_REPORT_ROUTE,
        query: launcherPlace.fetchQuery()
      });
    }
  }

  async onExport(format: string, downloadPurpose: string) {
    if (!this.tempPrintId) {
      useErrorsStore().pushErrorStr(Translator.translateId("youDoNotHaveAdobeReaderPlugin"));
      return;
    }
    if (downloadPurpose == DownloadPurpose.PRINT && ClientCache.shouldPrintUsingNamaServer()) {
      let printingParams: PrintingParams = {tempPrintId: this.tempPrintId, printerName: this.printerName};
      await NamaPrinting.printUsingPrintingServer([printingParams]);
      return;
    }
    let url: string = ReportViewerPresenter.rptUrl(this.fetchPlace().reportId, this.tempPrintId, '', format, NamaPrinting.getDirectPrintOption(), downloadPurpose, this.printerName);
    NamaPrinting.printNext([url], 0);
  }

 async emailResult() {
    let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
      if (!record['outputFormat'])
        record['outputFormat'] = RepOutputFormat.PDF;
      let request: DTOEmailSendRequest = {
        temPrintId: this.reportId,
        outputFormat: record['outputFormat'],
      };
      let settings: AxiosRequestConfig = {
        data: NamaCommon.stringify(request)
      };
      let response = await NamaCommon.namaFetch(NamaURLS.SendEmailWithResult, settings);
      if (response && NamaCommon.isSuccess(response))
        useMessagesStore().pushMessageStr(Translator.translateId('sendIsScheduled'));
    });
   let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
   });
   let questionFields: QuestionField[] = [];
   ReportViewerPresenter.addEmailSendDialogFields(questionFields, EmailSenderDialogType.SendReport);
   let defaultValues: DTOFilledQuestionField[] = [
     {id: IdsAndConstants.outputFormat, value: RepOutputFormat.PDF, objValue: RepOutputFormat.PDF}
   ];
   let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("emailSend"), false);
   await dialogBox.createPresenter(this, questionFields, defaultValues);
   await dialogBox.runDialogBox();
  }

  async onEditParams() {
    let launcherPlace: ReportLauncherPlace = new ReportLauncherPlace();
    launcherPlace.reportId = this.fetchPlace().reportId;
    launcherPlace.questions = this.fetchPlace().questions;
    let reportLauncherPresenter: ReportLauncherPresenter = new ReportLauncherPresenter(launcherPlace);
    await reportLauncherPresenter.init(this.reportMetaDataContainer?.metadata ?? {});
    let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
      reportLauncherPresenter.questionFieldsEditor.record.value = NamaCommon.cloneObject(record);
      await this.applyReportParams(reportLauncherPresenter);
    });
    let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
    });
    let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateRef(this.reportMetaDataContainer?.report ?? {}), false);
    await dialogBox.createPresenter(this, reportLauncherPresenter.questionFieldsEditor.questionFields, reportLauncherPresenter.questionFieldsEditor.defaultValues);
    await dialogBox.runDialogBox();
  }

  async applyReportParams(embeddedLauncherPresenter: ReportLauncherPresenter) {
    let reportResultPlace: ReportViewerPlace = embeddedLauncherPresenter.createReportResultPlace();
    embeddedLauncherPresenter.fetchPlace().reportId = reportResultPlace.reportId;
    embeddedLauncherPresenter.fetchPlace().questions = reportResultPlace.questions;
    if (!embeddedLauncherPresenter.isDashboardType())
      reportResultPlace.format = this.fetchPlace().format;
    if (this.popupContainer) {
      this.popupContainer.place = reportResultPlace;
      this.popupContainer.currentMode.value = "launch";
      await nextTick();
      this.popupContainer.currentMode.value = "view";
    } else {
      await router.replace({
        query: reportResultPlace.fetchQuery(),
        name: RouteNames.REPORT_RESULT_ROUTE,
      });
    }
  }

  static addEmailSendDialogFields(questionFields: namaTypes.QuestionField[], emailSenderDialogType: string) {
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.to1, emailSenderDialogType));
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.to2, emailSenderDialogType));
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.to3, emailSenderDialogType));
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.cc1, emailSenderDialogType));
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.cc2, emailSenderDialogType));
    questionFields.push(ReportViewerPresenter.addRef(IdsAndConstants.cc3, emailSenderDialogType));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.toEmail1, FieldType.Text));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.toEmail2, FieldType.Text));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.toEmail3, FieldType.Text));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.ccEmail1, FieldType.Text));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.ccEmail2, FieldType.Text));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.ccEmail3, FieldType.Text));
    let allowedFormats: string[] = [RepOutputFormat.PDF, RepOutputFormat.PPTX, RepOutputFormat.ODT, RepOutputFormat.XLS, RepOutputFormat.XLSX, RepOutputFormat.RTF, RepOutputFormat.DOCX, RepOutputFormat.HTML, RepOutputFormat.ODS, RepOutputFormat.TXT];
    questionFields.push(NamaCommon.createQuestionFieldWithAllowedValues(IdsAndConstants.outputFormat, FieldType.Enum, allowedFormats));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.subject, FieldType.Text, EditScreenFieldLayout.spanned));
    questionFields.push(NamaCommon.createQuestionField(IdsAndConstants.content, FieldType.BigText, EditScreenFieldLayout.spanned2, true));
  }

  private static addRef(id: string, type: string): QuestionField {
    let allowedTypes: string[] = [DomainBaseEntities.Employee, BSCEntities.Customer];
    let emailSendToTypes: KeyList[] | undefined = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.emailSendToTypes;
    let emailSendToType: KeyList | undefined = emailSendToTypes ? emailSendToTypes.find(k => k.key === id || k.key === (type + "#" + id)) : undefined;
    if (emailSendToType && emailSendToType.values) {
      allowedTypes.push(...emailSendToType.values);
    }
    return NamaCommon.createQuestionFieldWithAllowedValues(id, FieldType.Genericreference, allowedTypes);
  }

  override createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo {
    return this.fetchDispatcher().createSearchContextInfo(fieldId, lineNumber);
  }

  async fetchReportRef() {
    if (this.reportMetaDataContainer)
      return;
    let request: ByIdRequest = {
      entityType: DomainBaseEntities.ReportDefinition,
      id: this.fetchPlace().reportId
    };
    let settings: AxiosRequestConfig = {
      data: request
    };
    let response = await NamaCommon.namaFetch(NamaURLS.FetchReportMetadata, settings);
    if (response && response.result && response.result.succeded)
      this.reportMetaDataContainer = response.data;
  }

  async createEditParams(): Promise<ReportLauncherPresenter> {
    let launcherPlace: ReportLauncherPlace = new ReportLauncherPlace();
    launcherPlace.reportId = this.fetchPlace().reportId;
    launcherPlace.questions = this.fetchPlace().questions;
    let reportLauncherPresenter: ReportLauncherPresenter = new ReportLauncherPresenter(launcherPlace, this.popupContainer);
    await reportLauncherPresenter.init(this.reportMetaDataContainer?.metadata ?? {});
    return reportLauncherPresenter;
  }
}
